import { DateTime, Duration } from "luxon";
import Tag from "@/inside/models/Action/Tag";

/**
 * @property {integer|null} id
 * @property {DateTime} date.due
 * @property {DateTime|null} date.done
 * @property {string|null} type
 * @property {string|null} title
 * @property {string|null} means.human
 * @property {string|null} means.technical
 * @property {string|null} means.financial
 * @property {string} status
 * @property {string|null} content
 * @property {string|null} comment
 * @property {string} priority
 * @property {[object]} assignees
 * @property {integer} nbRequirements
 * @property {boolean} effectivenessIsVerified
 * @property {Textbase|null} textbase
 */
export default class Action {
    constructor() {
        this.id = null;
        this.textbase = null;
        this.date = {
            due: DateTime.now().plus({days: 7}),
            done: null
        };
        this.type = null;
        this.title = null;
        this.means = {
            human: null,
            technical: null,
            financial: null
        };
        this.status = "planned";
        this.content = null;
        this.comment = null;
        this.priority = "low";
        this.assignees = [];
        this.nbRequirements = 0;
        this.effectivenessIsVerified = false;
        this.tags = [];
    }

    /**
     * Adds a new "blank" assignee to this action.
     *
     * @return {object}
     */
    addAssignee(){
        let assignee = {
            lastname: null,
            firstname: null,
            email: null,
            id: Math.floor((1 + Math.random()) * 0x10000)
        };

        this.assignees.push(assignee);
        return assignee;
    }

    /**
     * Instantiates a new instance of Action class and hydrates it using given api_resource data.
     *
     * @param {object} api_resource
     * @return {Action}
     */
    static fromAPI(api_resource) {
        let out = new Action();
        Object.assign(out, {
            id: api_resource.id,
            type: api_resource.type,
            title: api_resource.title.trim(),
            status: api_resource.status,
            content: api_resource.content,
            comment: api_resource.comment,
            priority: api_resource.priority,
            assignees: api_resource.assignees,
            nbRequirements: api_resource.nbRequirements,
            effectivenessIsVerified: api_resource.effectivenessIsVerified,
            tags: api_resource.tags.map((tag_data) => {
                return Tag.fromAPI(tag_data);
            }),
            date: {
                due: DateTime.fromFormat(api_resource.date.due, "yyyy-MM-dd"),
                done: api_resource.date.done ?
                DateTime.fromFormat(api_resource.date.done, "yyyy-MM-dd"): null
            }
        });

        Object.assign(out.means, api_resource.means);

        return out;
    }

    /**
     * status of the deadline
     *
     * @return {String}
     */
    get schedule() {
        if ([Action.statusPlanned, Action.statusInProgress].includes(this.status)) {
            const dateDue = this.date.due.valueOf();
            const dateLessMonth = this.date.due.minus(Action.scheduleWarningDuration).valueOf();
            const now = DateTime.now().valueOf();
            if(now < dateLessMonth){
                return Action.scheduleEarly;
            }else if(now > dateLessMonth && now < dateDue){
                return Action.scheduleWarning;
            }
            return Action.scheduleOverdue;
        } else {
            return Action.scheduleFinish;
        }
    }

    get assigneeNames() {
        return this.assignees.filter((assignee) => {
            return assignee.lastname !== null;
        }).reduce((accumulator, assignee) => {
            return (accumulator === "")
                ? assignee.lastname
                : `${accumulator}, ${assignee.lastname}`;
        }, "");
    }

    /**
     *
     * @param {string} candidate
     * @return {boolean}
     */
    static isValidPriority(candidate) {
        return [Action.priorityHigh, Action.priorityLow].includes(candidate);
    }

    /**
     *
     * @return {string}
     */
    static get priorityHigh() {
        return "high";
    }

    /**
     *
     * @return {string}
     */
    static get priorityLow() {
        return "low";
    }

    /**
     * @returns {Duration}
     */
    static get scheduleWarningDuration(){
        return Duration.fromObject({months: 2});
    }
    /**
     * @returns {string}
     */
    static get scheduleEarly(){
        return "early";
    }
    /**
     * @returns {string}
     */
     static get scheduleWarning(){
        return "warning";
    }
    /**
     * @returns {string}
     */
     static get scheduleOverdue(){
        return "overdue";
    }
    /**
     * @returns {string}
     */
     static get scheduleFinish(){
        return "finish";
    }
    /**
     * @returns {string}
     */
    static get statusClosed() {
        return "closed";
    }

    /**
     * @returns {string}
     */
    static get statusInProgress() {
        return "in_progress";
    }

    /**
     * @returns {string}
     */
    static get statusPlanned() {
        return "planned";
    }

    toJSON() {
        return {
            textbase: this.textbase,
            date: {
                due: this.date.due ? this.date.due.toFormat("yyyy-MM-dd") : null,
                done: this.date.done ? this.date.done.toFormat("yyyy-MM-dd") : null
            },
            type: this.type,
            title: this.title,
            means: this.means,
            status: this.status,
            content: this.content,
            comment: this.comment,
            priority: this.priority,
            assignees: this.assignees.filter((assignee) => {
                return assignee.firstname || assignee.lastname;
            }),
            tags: this.tags
        };
    }
}
